import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Link, useHistory, useLocation, useParams } from "react-router-dom";

import useUpdateFeedResource from "@fartherfinance/frontend/api/Notifications/hooks/useUpdateFeedResource";
import { FeedStatus } from "@fartherfinance/frontend/api/Notifications/Types";
import useGetTaskV3 from "@fartherfinance/frontend/api/Tasks/hooks/useGetTask.v3";
import useGetTaskActivityLog from "@fartherfinance/frontend/api/Tasks/hooks/useGetTaskActivityLog";
import useGetTaskComments from "@fartherfinance/frontend/api/Tasks/hooks/useGetTaskComments";
import useGetTask from "@fartherfinance/frontend/api/TaskWorkflows/hooks/useGetTask";
import useGetTaskTemplate from "@fartherfinance/frontend/api/TaskWorkflows/hooks/useGetTaskTemplate";
import { TaskDetails } from "@fartherfinance/frontend/api/TaskWorkflows/Types";
import {
  AdvisorRequestConfig,
  TaskId,
} from "@fartherfinance/frontend/api/Types";

import { TASK_COMMENTS_STATIC_QUERY_PARAMS } from "../../Tasks/common";
import { useTasksNavigation } from "../../Tasks/hooks";
import TaskActivityV2 from "../../Tasks/TaskDetails/Components/TaskActivity/TaskActivityV2";
import TaskAttachments from "../../Tasks/TaskDetails/Components/TaskAttachments/TaskAttachments";
import TaskDrawerFooter from "../../Tasks/TaskDrawerFooter/TaskDrawerFooter";
import { TaskPermissionsProvider } from "../../Tasks/taskPermissionsContext";
import TaskWorkflows from "../../TaskWorkflows";
import TaskActionButtons from "../components/TaskActionButtons";
import { SIMPLE_TEMPLATE_ID } from "../constants";
import { FormEngine } from "../FormEngine";
import {
  FormSubmitProvider,
  useFormSubmit,
} from "../FormEngine/context/FormSubmitContext";
import TaskMessageModal from "../TaskMessageModal/TaskMessageModal";
import TaskSubtasksWorkflow from "../TaskSubtasksWorkflow/TaskSubtasksWorkflow";
import TaskWorkflowActions from "../TaskWorkflowActions/TaskWorkflowActions";
import Drawer from "@src/multiCustodian/components/Drawer/Drawer";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import LogoLoadingStill from "@src/sharedComponents/LogoLoadingStill/LogoLoadingStill";
import Text from "@src/sharedComponents/Typography/Text";

import styles from "./TaskDetailsWorkflow.module.css";

// Tagged union for location state (matching CreateTaskWorkflow pattern)
interface CreateTaskLocationState {
  type: "createTask";
  data: {
    taskType?: string;
    taskTypeId?: number;
    client?: string;
    taskTypeTemplate?: string;
  };
}

interface SubtaskLocationState {
  type: "subtask";
  taskDetails: TaskDetails;
}

type LocationState = CreateTaskLocationState | SubtaskLocationState | undefined;

interface TaskDetailsWorkflowProps {
  disableDrawerTransition?: boolean;
}

const TaskDetailsWorkflowWithModal: React.FC<TaskDetailsWorkflowProps> = ({
  disableDrawerTransition,
}) => {
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [shouldCloseDrawer, setShouldCloseDrawer] = useState(false);

  const handleConfirmClose = (): void => {
    setIsConfirmModalOpen(false);
    setShouldCloseDrawer(true);
  };

  const handleCancelClose = (): void => {
    setIsConfirmModalOpen(false);
  };

  const handleShowConfirmModal = (): void => {
    setIsConfirmModalOpen(true);
  };

  const handleDrawerClosed = (): void => {
    setShouldCloseDrawer(false);
  };

  return (
    <TaskDetailsWorkflowInner
      disableDrawerTransition={disableDrawerTransition}
      onShowConfirmModal={handleShowConfirmModal}
      shouldCloseDrawer={shouldCloseDrawer}
      onDrawerClosed={handleDrawerClosed}
      isConfirmModalOpen={isConfirmModalOpen}
      onConfirmModalClose={handleCancelClose}
      onConfirmModalConfirm={handleConfirmClose}
    />
  );
};

interface TaskDetailsWorkflowInnerProps extends TaskDetailsWorkflowProps {
  onShowConfirmModal: () => void;
  shouldCloseDrawer: boolean;
  onDrawerClosed: () => void;
  isConfirmModalOpen: boolean;
  onConfirmModalClose: () => void;
  onConfirmModalConfirm: () => void;
}

const TaskDetailsWorkflowInner: React.FC<TaskDetailsWorkflowInnerProps> = ({
  disableDrawerTransition: _disableDrawerTransition = false,
  onShowConfirmModal,
  shouldCloseDrawer,
  onDrawerClosed,
  isConfirmModalOpen,
  onConfirmModalClose,
  onConfirmModalConfirm,
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);
  const { taskId } = useParams<Record<"taskId", TaskId>>();
  const location = useLocation<LocationState>();
  const history = useHistory();

  const auth = useAdvisorRequestAuth();
  const callUpdateFeedResource = useUpdateFeedResource(auth);
  const statusNotification = useStatusNotification();
  const { handleNavigateToTasksList } = useTasksNavigation();
  const { isDirty } = useFormSubmit();

  // Fetch task data using both APIs
  // TaskWorkflows API for FormEngine
  const getTaskWorkflowQuery = useGetTask(taskId, auth);
  // Legacy API for existing components (TaskActivity, TaskAttachments)
  const getTaskV3Query = useGetTaskV3(taskId, auth);
  const { data: taskActivities } = useGetTaskActivityLog(
    taskId,
    auth as AdvisorRequestConfig
  );
  const getTaskCommentsQuery = useGetTaskComments(
    taskId,
    TASK_COMMENTS_STATIC_QUERY_PARAMS,
    auth
  );

  // Use the simple-template ID for now, this could be configurable in the future
  const {
    data: template,
    isLoading: isTemplateLoading,
    hasError: templateHasError,
  } = useGetTaskTemplate(SIMPLE_TEMPLATE_ID, auth);

  const {
    data: taskWorkflowData,
    isLoading: isTaskWorkflowLoading,
    hasError: taskWorkflowHasError,
  } = getTaskWorkflowQuery;

  const {
    data: taskDetailsV3,
    isLoading: isTaskV3Loading,
    hasError: taskV3HasError,
  } = getTaskV3Query;

  const { data: taskComments } = getTaskCommentsQuery;

  const isLoading =
    isTaskWorkflowLoading || isTaskV3Loading || isTemplateLoading;
  const hasError = taskWorkflowHasError || taskV3HasError || templateHasError;

  const handleCloseDrawer = useCallback((): void => {
    if (isDirty) {
      onShowConfirmModal();
    } else {
      setIsDrawerOpen(false);
    }
  }, [isDirty, onShowConfirmModal]);

  useEffect(() => {
    if (shouldCloseDrawer) {
      setIsDrawerOpen(false);
      onDrawerClosed();
    }
  }, [shouldCloseDrawer, onDrawerClosed]);

  const handleFormSubmit = useCallback(
    (_formData: unknown): void => {
      statusNotification("Task updated.", "Success");
      setIsDrawerOpen(false);
    },
    [statusNotification]
  );

  const handleFormError = useCallback((_error: Error): void => {
    // TODO: Show error notification
  }, []);

  // Mark feed resource as viewed
  useEffect(() => {
    const updateFeedResource = async (): Promise<void> => {
      const personId = auth?.advisorId;

      if (personId && taskId) {
        try {
          await callUpdateFeedResource({
            resourceId: taskId,
            resourceType: "task",
            personId,
            request: { status: FeedStatus.enum.READ },
          });
        } catch {
          // NOTE: fail silently, user should not be aware of this call
        }
      }
    };

    updateFeedResource();

    // NOTE: we just want to run this effect once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle navigation when drawer closes
  useEffect(() => {
    if (!isDrawerOpen) {
      handleNavigateToTasksList();
    }
  }, [isDrawerOpen, handleNavigateToTasksList]);

  const initialValues = useMemo(() => {
    const locationState = location.state;

    if (locationState?.type === "subtask") {
      return locationState.taskDetails.fields;
    }

    if (locationState?.type === "createTask") {
      const { data } = locationState;
      return {
        taskType: data.taskType,
        client: data.client,
      };
    }

    return taskWorkflowData?.fields ?? undefined;
  }, [location.state, taskWorkflowData?.fields]);

  // Memoize arrays and objects to prevent unnecessary re-renders
  const taskCommentsArray = useMemo(
    () => taskComments?.values || [],
    [taskComments?.values]
  );

  const taskAttachmentsArray = useMemo(
    () => taskWorkflowData?.attachments || [],
    [taskWorkflowData?.attachments]
  );

  const externalResources = useMemo(() => ({ userRoles: ["cx"] }), []);

  // Create footer with TaskDrawerFooter or Cancel/Save buttons
  const footer = useMemo(() => {
    if (!taskId) {
      return undefined;
    }

    return isDirty ? (
      <TaskActionButtons onCancel={handleCloseDrawer} />
    ) : (
      <TaskDrawerFooter taskId={taskId} />
    );
  }, [taskId, isDirty, handleCloseDrawer]);
  return (
    <TaskWorkflows>
      <Drawer
        isDrawerOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        footer={footer}
      >
        {isLoading && (
          <div className={styles.loading}>
            <LogoLoadingStill />
          </div>
        )}

        {hasError && (
          <div className={styles.error}>
            <Text variant="body">Error loading task details</Text>
          </div>
        )}

        {taskWorkflowData &&
          taskDetailsV3 &&
          template &&
          !isLoading &&
          !hasError && (
            <TaskPermissionsProvider
              comments={taskCommentsArray}
              attachments={taskAttachmentsArray}
              taskId={taskWorkflowData.id}
            >
              {/* Header Section - Parent Task Link and Actions */}
              <div className={styles.headerSection}>
                <div className={styles.parentTaskContainer}>
                  {taskWorkflowData.parent && (
                    <Link
                      className={styles.parentTaskLink}
                      to={{
                        pathname: `/Advisor/Tasks/${taskWorkflowData.parent.id}`,
                        search: history.location.search,
                      }}
                    >
                      <Text variant="body" color="textSubtle">
                        Parent Task: {taskWorkflowData.parent.title}
                      </Text>
                    </Link>
                  )}
                </div>

                <div className={styles.actionsSection}>
                  <TaskWorkflowActions taskDetails={taskWorkflowData} />
                </div>
              </div>

              {/* Form Engine Section */}
              <div className={styles.formSection}>
                <FormEngine
                  template={template}
                  onSubmit={handleFormSubmit}
                  onError={handleFormError}
                  initialValues={initialValues}
                  externalResources={externalResources}
                />
              </div>

              {/* Subtasks Section */}
              {taskWorkflowData && (
                <div className={styles.subtasksSection}>
                  <TaskSubtasksWorkflow taskWorkflowData={taskWorkflowData} />
                </div>
              )}

              {/* Attachments Section */}
              {taskAttachmentsArray.length > 0 && (
                <div className={styles.attachmentsSection}>
                  <TaskAttachments taskDetails={taskDetailsV3} />
                </div>
              )}

              {/* Activity Section */}
              <div className={styles.activitySection}>
                <TaskActivityV2
                  taskDetails={taskDetailsV3}
                  taskActivities={taskActivities}
                />
              </div>
            </TaskPermissionsProvider>
          )}

        {!isLoading && !hasError && !template && (
          <div className={styles.error}>
            <Text variant="body">No template available for this task</Text>
          </div>
        )}

        <TaskMessageModal
          title="Are you sure you want to leave without saving?"
          isOpen={isConfirmModalOpen}
          onClose={{
            label: "Stay",
            action: onConfirmModalClose,
          }}
          onConfirm={{
            label: "Leave",
            action: onConfirmModalConfirm,
          }}
        >
          <Text variant="body" color="textSubtle">
            Your changes will not be saved.
          </Text>
        </TaskMessageModal>
      </Drawer>
    </TaskWorkflows>
  );
};

const TaskDetailsWorkflow: React.FC<TaskDetailsWorkflowProps> = (props) => {
  return (
    <FormSubmitProvider>
      <TaskDetailsWorkflowWithModal {...props} />
    </FormSubmitProvider>
  );
};

export default TaskDetailsWorkflow;
